import { useState, useEffect } from "react";
import styles from "./list.module.css";
import { TextField, Button, Table, TableCell, TableHead, TableContainer, Paper, TableBody, TableRow, Dialog, Snackbar, Alert, Switch } from "@mui/material";
import TablePaginationComponent from "../../components/TablePagination";
import { Add, Close } from "@mui/icons-material";
import axios from "axios";
import DeletePopup from "../../components/DeletePopup";
import Loader from "../../components/Loader";
import { authData, withAdminAuth } from "../../components/GetAuth";
import { Edit, Search, Trash2 } from "react-feather";
import AddMembershipForm from "../../components/popups/AddMembershipForm";
import PageHeader from "../../components/PageHeader";
import { useWebsite } from "../../../context/WebsiteContext";
import { useSiteBranding } from "../../../hooks/useSiteBranding";
import MembershipListLoader from "./MembershipListLoader";

const MembershipList = () => {
  const [loading, setLoading] = useState(false);
  const [contentLoading, setContentLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [allData, setAllData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [formOpen, setFormOpen] = useState(false);
  const [filterVars, setFilterVars] = useState({ searchKey: "" });
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarType, setSnackbarType] = useState("success");
  const [deleteModalOpen, setDeleteModalOpen] = useState();
  const [deleteId, setDeleteId] = useState(0);
  const [editData, setEditData] = useState(null);
  const auth = authData();
  const API_TOKEN = auth?.api_token;

  const { websiteData } = useWebsite();
  useSiteBranding("Membership list", websiteData);

  const canManage = auth?.user_type === 2 || auth?.permissions?.indexOf("settings:update-membership") > -1;
  const canAdd = auth?.user_type === 2 || auth?.permissions?.indexOf("settings:add-membership") > -1;
  const columnCount = canManage ? 7 : 6;

  const paginationChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const paginationChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  useEffect(() => {
    const fetchSubscriptions = () => {
      setContentLoading(true);
      axios
        .get(process.env.REACT_APP_API_URL + "v1/memberships", {
          headers: {
            Authorization: `Bearer ${API_TOKEN}`,
          },
        })
        .then((response) => {
          setContentLoading(false);
          setAllData(response.data.data);
        })
        .catch((error) => {
          setContentLoading(false);
        });
    };

    fetchSubscriptions();
  }, [API_TOKEN]);

  useEffect(() => {
    let filterredTempData = allData;

    if (filterVars?.searchKey !== "") {
      let inputVal = filterVars?.searchKey;
      inputVal = inputVal.toLowerCase();
      filterredTempData = filterredTempData.filter((item) => {
        return (
          item.membership_id.toLowerCase().includes(inputVal) ||
          item.name.toLowerCase().includes(inputVal) ||
          item.duration.toLowerCase().includes(inputVal)
        );
      });
    }

    setFilteredData(filterredTempData);
  }, [allData, filterVars]);

  const deleteConfirm = () => {
    setLoading(true);
    setDeleteModalOpen(false);
    axios
      .delete(process.env.REACT_APP_API_URL + "v1/memberships/" + deleteId, {
        headers: {
          Authorization: `Bearer ${API_TOKEN}`,
        },
      })
      .then((response) => {
        setLoading(false);
        if (response.data.status === "SUCCESS") {
          setSnackbarOpen(true);
          setSnackbarMessage(response.data.message);
          setSnackbarType("success");
          setAllData((prev) => {
            let temp = prev.filter((i) => i.id !== deleteId);
            return temp;
          });
          setDeleteId(0);
        }
        if (response.data.status === "ERROR") {
          setSnackbarOpen(true);
          setSnackbarMessage(response.data.message);
          setSnackbarType("error");
        }
      })
      .catch((error) => {
        setLoading(false);
        if (error && error.response && error.response.data && error.response.data.messages) {
          setSnackbarOpen(true);
          setSnackbarMessage(error.response.data.messages);
          setSnackbarType("error");
        }
      });
  };

  const statusChange = (e, item) => {
    let status = 0;
    if (e.target.checked) {
      status = 1;
    }
    setLoading(true);

    axios
      .put(
        process.env.REACT_APP_API_URL + "v1/memberships/" + item?.id,
        { status: status },
        {
          headers: {
            Authorization: `Bearer ${API_TOKEN}`,
          },
        }
      )
      .then((response) => {
        setLoading(false);

        if (response.data.status === "SUCCESS") {
          setSnackbarOpen(true);
          setSnackbarMessage(response.data.message);
          setSnackbarType("success");

          setAllData((prev) => {
            let temp = prev.map((i) => {
              if (i.id === item?.id) {
                return { ...item, status: status };
              }
              return i;
            });
            return temp;
          });
        }

        if (response.data.status === "ERROR") {
          setSnackbarOpen(true);
          setSnackbarMessage(response.data.message);
          setSnackbarType("error");
        }
      })
      .catch((error) => {
        setLoading(false);
        if (error && error.response && error.response.data && error.response.data.messages) {
          setSnackbarOpen(true);
          setSnackbarMessage(error.response.data.messages);
          setSnackbarType("error");
        }
      });
  };

  const formatPrice = (item) => (item.price ? <>&pound;{parseFloat(item.price).toFixed(2)}</> : "-");

  const pagedData = filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <>
      <Loader open={loading} setOpen={setLoading.bind(this)} />
      <div className={styles.MainDiv}>
        <PageHeader title="Memberships" trail={[{ label: "List" }]} />

        <div className={styles.MainCard}>
          {contentLoading && <MembershipListLoader rows={rowsPerPage} />}

          {!contentLoading && (
            <>
              <div className={styles.TableFilterHead}>
                <div className={styles.DataTableSearch}>
                  <Search className={styles.SearchIcon} />
                  <TextField
                    id="search"
                    className={styles.SearchInput}
                    variant="standard"
                    InputProps={{ disableUnderline: true }}
                    type="text"
                    placeholder="Search"
                    aria-label="Search Input"
                    value={filterVars?.searchKey}
                    onChange={(e) =>
                      setFilterVars((prev) => {
                        return { ...prev, searchKey: e.target.value };
                      })
                    }
                  />
                  {filterVars?.searchKey !== "" && (
                    <Button
                      type="button"
                      className={styles.CloseBU}
                      onClick={(e) =>
                        setFilterVars((prev) => {
                          return { ...prev, searchKey: "" };
                        })
                      }
                    >
                      <Close />
                    </Button>
                  )}
                </div>
                {canAdd && (
                  <div className={styles.TableFilterBUSec}>
                    <Button
                      className={styles.TableAddBU}
                      onClick={(e) => {
                        setFormOpen(true);
                        setEditData(null);
                      }}
                    >
                      <Add /> Item
                    </Button>
                  </div>
                )}
              </div>

              {/* ============ Desktop table ============ */}
              <div className={styles.CusTable}>
                <TableContainer component={Paper} elevation={0}>
                  <Table sx={{ minWidth: 650 }} aria-label="membership list table">
                    <TableHead>
                      <TableRow>
                        <TableCell className={styles.TableHeaderCell}>Membership Id</TableCell>
                        <TableCell className={styles.TableHeaderCell}>Name</TableCell>
                        <TableCell className={styles.TableHeaderCell}>Duration</TableCell>
                        <TableCell className={styles.TableHeaderCell}>Price</TableCell>
                        <TableCell className={styles.TableHeaderCell}>Discount</TableCell>
                        <TableCell className={styles.TableHeaderCell}>Status</TableCell>
                        {canManage && <TableCell className={styles.TableHeaderCell}></TableCell>}
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {pagedData.map((item, index) => {
                        return (
                          <TableRow key={index} className={styles.TableRow}>
                            <TableCell>{item?.membership_id}</TableCell>
                            <TableCell>{item?.name}</TableCell>
                            <TableCell>{item?.duration}</TableCell>
                            <TableCell>{formatPrice(item)}</TableCell>
                            <TableCell>{item?.discount}%</TableCell>
                            <TableCell>
                              <Switch
                                checked={item?.status}
                                onChange={(e) => statusChange(e, item)}
                                inputProps={{ "aria-label": "controlled" }}
                                disabled={!canManage}
                                className={styles.CustomSwitch}
                              />
                            </TableCell>
                            {canManage && (
                              <TableCell className={styles.ActionsCell}>
                                <Button
                                  onClick={(e) => {
                                    setFormOpen(true);
                                    setEditData(item);
                                  }}
                                  className={styles.TableActionBU}
                                >
                                  <Edit />
                                </Button>
                                <Button
                                  onClick={(e) => {
                                    setDeleteModalOpen(true);
                                    setDeleteId(item?.id);
                                  }}
                                  className={styles.TableActionBU}
                                >
                                  <Trash2 />
                                </Button>
                              </TableCell>
                            )}
                          </TableRow>
                        );
                      })}

                      {filteredData.length === 0 && (
                        <TableRow>
                          <TableCell className={styles.EmptyCell} colSpan={columnCount}>
                            No data found
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>

                {filteredData.length > 0 && (
                  <div className={styles.PaginationWrap}>
                    <TablePaginationComponent
                      count={filteredData.length}
                      page={page}
                      rowsPerPage={rowsPerPage}
                      handleChangePage={paginationChangePage}
                      handleChangeRowsPerPage={paginationChangeRowsPerPage}
                    />
                  </div>
                )}
              </div>

              {/* ============ Mobile card list ============ */}
              <div className={styles.CardList}>
                {pagedData.map((item, index) => (
                  <div key={index} className={styles.MembershipCard}>
                    <div className={styles.CardTopRow}>
                      <div className={styles.CardHeading}>
                        <p className={styles.CardName}>{item?.name}</p>
                        <p className={styles.CardMembershipId}>{item?.membership_id}</p>
                      </div>
                      <Switch
                        checked={item?.status}
                        onChange={(e) => statusChange(e, item)}
                        inputProps={{ "aria-label": "controlled" }}
                        disabled={!canManage}
                        className={styles.CustomSwitch}
                      />
                    </div>

                    <div className={styles.CardGrid}>
                      <div className={styles.CardField}>
                        <span className={styles.CardFieldLabel}>Duration</span>
                        <span className={styles.CardFieldValue}>{item?.duration}</span>
                      </div>
                      <div className={styles.CardField}>
                        <span className={styles.CardFieldLabel}>Price</span>
                        <span className={styles.CardFieldValue}>{formatPrice(item)}</span>
                      </div>
                      <div className={styles.CardField}>
                        <span className={styles.CardFieldLabel}>Discount</span>
                        <span className={styles.CardFieldValue}>{item?.discount}%</span>
                      </div>
                    </div>

                    {canManage && (
                      <div className={styles.CardFooterRow}>
                        <div className={styles.CardActions}>
                          <Button
                            onClick={(e) => {
                              setFormOpen(true);
                              setEditData(item);
                            }}
                            className={styles.TableActionBU}
                          >
                            <Edit />
                          </Button>
                          <Button
                            onClick={(e) => {
                              setDeleteModalOpen(true);
                              setDeleteId(item?.id);
                            }}
                            className={styles.TableActionBU}
                          >
                            <Trash2 />
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}

                {filteredData.length === 0 && <div className={styles.EmptyState}>No data found</div>}

                {filteredData.length > 0 && (
                  <div className={styles.PaginationWrap}>
                    <TablePaginationComponent
                      count={filteredData.length}
                      page={page}
                      rowsPerPage={rowsPerPage}
                      handleChangePage={paginationChangePage}
                      handleChangeRowsPerPage={paginationChangeRowsPerPage}
                    />
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      <Dialog open={formOpen} onClose={(e) => setFormOpen(false)} fullWidth maxWidth="sm">
        <AddMembershipForm
          setFormOpen={setFormOpen.bind(this)}
          setAllData={setAllData.bind(this)}
          setSnackbarOpen={setSnackbarOpen.bind(this)}
          setSnackbarMessage={setSnackbarMessage.bind(this)}
          setSnackbarType={setSnackbarType.bind(this)}
          setLoading={setLoading.bind(this)}
          editData={editData}
        />
      </Dialog>

      <DeletePopup open={deleteModalOpen} setOpen={setDeleteModalOpen.bind(this)} confirmAction={deleteConfirm.bind(this)} />

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={(e) => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert onClose={(e) => setSnackbarOpen(false)} severity={snackbarType} variant="filled" sx={{ width: "100%" }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default withAdminAuth(MembershipList);